/**
 * useDocumentTitle Hook
 * 
 * Automatically updates the browser tab title based on the current route.
 * Respects Helmet usage - if a page uses Helmet to set a title, it will take precedence.
 * 
 * Usage:
 *   import { useDocumentTitle } from '../hooks/useDocumentTitle';
 *   useDocumentTitle(); // In your component
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteTitle } from '../config/routeTitles';
import { BRANDING } from '../config/branding';

/**
 * Hook to automatically update document title based on current route
 * 
 * This hook watches route changes and updates the browser tab title accordingly.
 * If a page uses react-helmet-async to set a title, Helmet will update it after
 * this effect runs, so Helmet titles will take precedence.
 * 
 * We use a small delay to allow Helmet to set the title first if present,
 * then update if the title hasn't been customized by Helmet.
 */
export const useDocumentTitle = (): void => {
  const location = useLocation();

  useEffect(() => {
    // The dependency array already limits re-runs to pathname changes; a
    // previousPathRef guard here would skip the StrictMode remount and leave
    // the default title in place on first load.

    // Get the expected title for this route
    const routeTitle = getRouteTitle(location.pathname);

    // Use a small delay to allow Helmet to set title first if present
    // Helmet updates happen during render/effects, so we give it a moment
    const timeoutId = setTimeout(() => {
      const currentTitle = document.title;

      // Update title if it hasn't been customized by Helmet
      // We check if the title matches our expected format or is still the default
      // If Helmet set a custom title, it will be different and we won't override
      if (currentTitle === BRANDING.meta.defaultTitle ||
          currentTitle.endsWith(' - OneCare') ||
          currentTitle.startsWith('Sign In') ||
          currentTitle === routeTitle) {
        document.title = routeTitle;
      }
    }, 100); // Small delay to allow Helmet to set title first

    // Cleanup timeout on unmount or path change
    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname]);
};
